import { zodResolver } from '@hookform/resolvers/zod';
import { createConversationSchema } from 'Modules/conversation/createConversation/components/createConversationForm/schema';
import type { ContactOptionType } from 'Modules/conversation/createConversation/types';
import { useConversationStore } from 'Modules/conversation/store/index.store';
import { useEffect } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { bugsnagClient } from 'Utils/logUtils';
import { z } from 'zod';

type CreateConversationFormType = z.infer<typeof createConversationSchema>;

interface UseConversationFormEffectsProps {
  selectedContacts: ContactOptionType[];
  isError: boolean;
}

/**
 * This hook is used to handle the effects of the conversation form.
 * It is used to reset the form values, handle errors, and handle selected contacts changes.
 */
export const useConversationFormEffects = ({
  selectedContacts,
  isError,
}: UseConversationFormEffectsProps): UseFormReturn<CreateConversationFormType> => {
  const { setPreviewConversationId } = useConversationStore();

  const form = useForm<CreateConversationFormType>({
    resolver: zodResolver(createConversationSchema),
    defaultValues: {
      groupName: '',
      groupDescription: '',
      selectedContacts: [],
    },
    mode: 'onChange',
  });

  // Effect to handle selected contacts changes
  useEffect(() => {
    if (selectedContacts.length === 0) {
      form.setValue('selectedContacts', []);
      setPreviewConversationId(null);
    }
  }, [selectedContacts, form, setPreviewConversationId]);

  // Effect to handle error state
  useEffect(() => {
    if (isError) {
      // Inform problem on the autocomplete input
      form.setError('selectedContacts', {
        message: 'Error fetching contacts',
      });

      bugsnagClient.notify(new Error('Error fetching contacts'), (event) => {
        event.severity = 'error';
        event.context = 'CreateConversationForm';
        event.addMetadata('custom', {
          function: 'loadMoreContacts',
        });
      });
    }
  }, [isError, form]);

  return form;
};
